import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { useSession } from '@/features/auth/use-session'
import { toSettingsUser, toStageSet } from '@/shared/api/adapt'
import { api } from '@/shared/api/client'
import type {
  AgentRead,
  AgentTokenCreated,
  Page,
  StageSetRead,
  UserListItem,
  UserRead,
} from '@/shared/api/contracts'
import { isApiError } from '@/shared/api/errors'
import { qk } from '@/shared/api/query-keys'
import { errorMessage } from '@/shared/components/data'
import { useT } from '@/shared/i18n'
import { toSearchParams } from '@/shared/lib/query-params'
import type { Uuid } from '@/shared/types/domain'

import type {
  StagePatchInput,
  StageSet,
  StageSetKind,
  StageSetting,
  UserInviteInput,
  UserPatchInput,
} from './types'

/* -------------------------------------------------------------- columnas -- */

export function useStageSets(kind: StageSetKind) {
  return useQuery({
    queryKey: qk.settings.stageSets({ kind, raw: true }),
    queryFn: () => api.get<StageSetRead[]>(`stage-sets?kind=${kind}`),
    select: (sets): StageSet[] => sets.map(toStageSet),
    // Pantalla de orden: un refetch al volver el foco con un drop en vuelo pisa
    // la cache optimista y la fila salta sola (plan 3.4).
    refetchOnWindowFocus: false,
  })
}

/**
 * Renombrar y recolorear una columna.
 *
 * Lo que este hook NO puede mandar es la `category`: `StagePatchInput` no la
 * tiene. Renombrar "En desarrollo" a "Cocinando" es cosmético y seguro;
 * cambiarle la categoría movería las reglas de notificación de todo lo que
 * tiene adentro, así que esa decisión no vive en una pantalla de edición
 * inline.
 */
export function useUpdateStage(kind: StageSetKind) {
  const client = useQueryClient()
  const t = useT()

  return useMutation({
    mutationFn: ({ stageId, patch }: { stageId: Uuid; patch: StagePatchInput }) =>
      api.patch<StageSetting>(`stage-sets/stages/${stageId}`, { ...patch }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.settings.stageSets({ kind, raw: true }) })
      // El tablero y los selectores de fase leen los mismos nombres.
      void client.invalidateQueries({ queryKey: qk.portfolio.all })
      void client.invalidateQueries({ queryKey: qk.projects.all })
    },
    onError: (error) => {
      toast.error(errorMessage(error, t('settings.error.saveStage')))
    },
  })
}

export interface ReorderStagesInput {
  stageSetId: Uuid
  /** La lista COMPLETA de ids, en el orden nuevo. */
  stageIds: Uuid[]
}

/**
 * Reordenar las columnas de un tirón.
 *
 * Acá no hay LexoRank y es a propósito: las columnas son cinco o seis, las
 * mueve un admin cada varios meses y no hay dos personas arrastrando a la vez.
 * Mandar la lista completa hace que el estado final sea explícito y que dos
 * PUT seguidos no puedan dejar un orden intermedio raro.
 */
export function useReorderStages(kind: StageSetKind) {
  const client = useQueryClient()
  const t = useT()
  const key = qk.settings.stageSets({ kind, raw: true })

  return useMutation({
    mutationFn: ({ stageSetId, stageIds }: ReorderStagesInput) =>
      api.put<StageSetRead['stages']>(`stage-sets/${stageSetId}/stages/order`, {
        stage_ids: stageIds,
      }),

    // El optimista corre sobre la forma CRUDA del backend, que es lo que
    // guarda la cache; el `select` de la query la convierte al bajar. Parchear
    // el modelo de pantalla acá dejaría la cache con dos formas mezcladas.
    onMutate: async ({ stageSetId, stageIds }) => {
      await client.cancelQueries({ queryKey: key })
      const previous = client.getQueryData<StageSetRead[]>(key)

      client.setQueryData<StageSetRead[]>(key, (sets) =>
        sets?.map((set) => {
          if (set.id !== stageSetId) return set
          const byId = new Map(set.stages.map((stage) => [stage.id, stage]))
          return {
            ...set,
            stages: stageIds
              .map((id, index) => {
                const stage = byId.get(id)
                return stage ? { ...stage, position: index } : null
              })
              .filter((stage): stage is StageSetRead['stages'][number] => stage !== null),
          }
        }),
      )

      return { previous }
    },

    onError: (error, _input, context) => {
      if (context?.previous) client.setQueryData(key, context.previous)
      toast.error(errorMessage(error, t('settings.error.saveOrder')))
    },

    onSettled: () => {
      void client.invalidateQueries({ queryKey: key })
    },
  })
}

/**
 * El `code` que manda el backend cuando la columna tiene ítems adentro.
 *
 * Se compara de verdad, y no sólo el `409`: un 409 puede venir de otra cosa
 * (una condición de carrera al reordenar, por ejemplo) y tratarlo todo como
 * "la columna no está vacía" convierte un error distinto en un mensaje
 * equivocado. El status se sigue aceptando como respaldo para el caso de un
 * proxy que se coma el cuerpo.
 */
export const STAGE_NOT_EMPTY = 'stage_not_empty'

export function isStageNotEmpty(error: unknown): boolean {
  if (!isApiError(error)) return false
  return error.code === STAGE_NOT_EMPTY || error.status === 409
}

export interface DeleteStageInput {
  stageId: Uuid
  /** A dónde se mueve lo que hay adentro. */
  moveTo?: Uuid | null
}

/**
 * Borrar una columna, diciendo a dónde va lo que hay adentro.
 *
 * El parámetro se llama **`reassign_to`**, no `move_to` — que es lo que el
 * frontend mandaba y el servidor ignoraba en silencio, dejando un 409
 * incomprensible o, cuando la columna estaba vacía, la ilusión de que andaba.
 *
 * El destino se manda **sólo si hace falta**: `StageRead.items_count` dice
 * cuántos ítems vivos hay, así que una columna vacía se borra sin preguntar
 * nada. El 409 del backend sigue siendo la segunda línea, para el caso de que
 * alguien mueva algo adentro con el diálogo abierto.
 */
export function useDeleteStage(kind: StageSetKind) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: ({ stageId, moveTo }: DeleteStageInput) =>
      api.delete<void>(`stage-sets/stages/${stageId}${toSearchParams({ reassign_to: moveTo })}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.settings.stageSets({ kind, raw: true }) })
      void client.invalidateQueries({ queryKey: qk.portfolio.all })
      void client.invalidateQueries({ queryKey: qk.projects.all })
      void client.invalidateQueries({ queryKey: qk.tasks.all })
    },
  })
}

/* -------------------------------------------------------------- usuarios -- */

export function useSettingsUsers() {
  return useQuery({
    queryKey: qk.settings.users({ scope: 'settings' }),
    queryFn: async () =>
      (await api.get<Page<UserListItem>>('users?limit=200')).items.map(toSettingsUser),
  })
}

export function useInviteUser() {
  const client = useQueryClient()
  const t = useT()

  return useMutation({
    mutationFn: async (input: UserInviteInput) =>
      toSettingsUser(await api.post<UserRead>('users/invite', { ...input })),
    onSuccess: (user) => {
      void client.invalidateQueries({ queryKey: qk.settings.all })
      toast.success(t('settings.users.invited', { email: user.email }))
    },
    onError: (error) => {
      toast.error(errorMessage(error, t('settings.error.invite')))
    },
  })
}

/**
 * Editar a alguien: su rol, si está activo, su idioma.
 *
 * **Si el editado es uno mismo, también se invalida `GET /auth/me`.** El idioma
 * con el que se dibuja la app no sale de este listado: sale de la sesión. Sin
 * esa segunda invalidación, elegir "Português" cambiaba el `<select>` y dejaba
 * la pantalla en español hasta el próximo refresco — el cambio parecía no haber
 * funcionado.
 */
export function useUpdateUser() {
  const client = useQueryClient()
  const t = useT()
  const { me } = useSession()

  return useMutation({
    mutationFn: async ({ userId, patch }: { userId: Uuid; patch: UserPatchInput }) =>
      toSettingsUser(await api.patch<UserRead>(`users/${userId}`, { ...patch })),
    onSuccess: (_user, { userId }) => {
      void client.invalidateQueries({ queryKey: qk.settings.all })
      if (userId === me?.id) void client.invalidateQueries({ queryKey: qk.auth.me() })
    },
    onError: (error) => {
      toast.error(errorMessage(error, t('settings.error.saveUser')))
    },
  })
}

/**
 * `PUT /users/{id}/companies` — el conjunto COMPLETO, no un delta.
 *
 * La pantalla edita una lista de checkboxes; mandar el estado final evita la
 * ventana en la que el usuario quedó con una empresa de más o de menos entre
 * dos requests.
 */
export function useSetUserCompanies() {
  const client = useQueryClient()
  const t = useT()

  return useMutation({
    mutationFn: ({ userId, companyIds }: { userId: Uuid; companyIds: Uuid[] }) =>
      api.put<Uuid[]>(`users/${userId}/companies`, { company_ids: companyIds }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.settings.all })
    },
    onError: (error) => {
      toast.error(errorMessage(error, t('settings.error.saveCompanies')))
    },
  })
}

/* --------------------------------------------------------------- agentes -- */

/**
 * Los agentes: usuarios que entran con un token (`Bearer pmt_...`) y sólo
 * pueden trabajar tareas. Ver `docs/agentes.md`.
 *
 * Activarlos, desactivarlos y cambiarles las empresas NO tiene hooks propios:
 * un agente es un usuario, y eso ya lo hacen `useUpdateUser` y
 * `useSetUserCompanies`, que invalidan todo `settings` —también esta lista—.
 */
export function useAgents() {
  return useQuery({
    queryKey: qk.settings.agents(),
    queryFn: () => api.get<AgentRead[]>('agents'),
  })
}

export function useCreateAgent() {
  const client = useQueryClient()
  const t = useT()

  return useMutation({
    mutationFn: (input: { display_name: string; company_ids: Uuid[] }) =>
      api.post<AgentRead>('agents', { ...input }),
    onSuccess: () => void client.invalidateQueries({ queryKey: qk.settings.agents() }),
    onError: (error) => toast.error(errorMessage(error, t('settings.error.saveAgent'))),
  })
}

/** Emite un token. El que vuelve en la respuesta es el único que va a existir en claro. */
export function useCreateAgentToken() {
  const client = useQueryClient()
  const t = useT()

  return useMutation({
    mutationFn: ({ agentId, name }: { agentId: Uuid; name: string }) =>
      api.post<AgentTokenCreated>(`agents/${agentId}/tokens`, { name }),
    onSuccess: () => void client.invalidateQueries({ queryKey: qk.settings.agents() }),
    onError: (error) => toast.error(errorMessage(error, t('settings.error.issueToken'))),
  })
}

export function useRevokeAgentToken() {
  const client = useQueryClient()
  const t = useT()

  return useMutation({
    mutationFn: ({ agentId, tokenId }: { agentId: Uuid; tokenId: Uuid }) =>
      api.delete<void>(`agents/${agentId}/tokens/${tokenId}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.settings.agents() })
      toast.success(t('settings.agents.revoked'))
    },
    onError: (error) => toast.error(errorMessage(error, t('settings.error.revokeToken'))),
  })
}
